import type { DeviceNetworkInterfaceReport, NetworkConfigResponse } from '@emma/api_client/types';
import { Box, Flex, IconButton, Tag, Text, Tooltip } from '@lego/klik-ui';
import { Edit, FileCircleQuestion, LockBold } from '@lego/klik-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { writeTableStateToUrl } from '@/components/common/table/store/tableUrlParams';
import type { DeviceNetworkConfigResponse } from '@/hooks/device';
import { CopyableText } from '../common/CopyableText';

export type InterfaceConfigInfo = {
  config: NetworkConfigResponse;
  assignment: DeviceNetworkConfigResponse;
};

type Props = {
  networkInterfaces: DeviceNetworkInterfaceReport[] | null;
  configsByInterface?: Map<string, InterfaceConfigInfo>;
  networkConfigsPath?: string;
  show?: 'all' | 'physical' | 'vlan';
};

const formatAddresses = (addresses: DeviceNetworkInterfaceReport['addresses']): string => {
  if (addresses.length === 0) return 'None';
  return addresses.map((addr) => `${addr.ip}/${addr.prefixLen}`).join(', ');
};

const operstateColorScheme = (operstate: string): string => {
  const normalized = operstate.toLowerCase();
  if (normalized === 'up') return 'success';
  if (normalized === 'down') return 'error';
  return 'gray';
};

const InterfaceCard = ({
  iface,
  showVlan,
  configInfo,
  networkConfigsPath,
}: {
  iface: DeviceNetworkInterfaceReport;
  showVlan: boolean;
  configInfo?: InterfaceConfigInfo;
  networkConfigsPath?: string;
}) => (
  <Box
    border="1px solid"
    borderColor="gray.200"
    borderRadius="md"
    maxWidth="400px"
    minWidth="280px"
    p={3}
  >
    <Flex align="center" gap={2} mb={2}>
      <Text fontWeight="bold">{iface.name}</Text>
      <Tag colorScheme={operstateColorScheme(iface.operstate)} size="sm">
        {iface.operstate}
      </Tag>
      <Box flex={1} />
      {iface.isPrimary && (
        <Tooltip label="Primary network interface">
          <Tag
            alignItems="center"
            borderColor="black"
            borderStyle="solid"
            borderWidth={1}
            colorScheme="light-blue"
            display={'flex'}
            paddingLeft={2}
            size="sm"
          >
            <LockBold boxSize={3} mr={1} />
            <Tag.Label fontSize={'xs'} paddingTop={'1px'}>
              Primary
            </Tag.Label>
          </Tag>
        </Tooltip>
      )}
      {networkConfigsPath &&
        !iface.isPrimary &&
        (configInfo ? (
          <Tooltip label={`Config: ${configInfo.config.label} (${configInfo.config.fileName})`}>
            <IconButton
              aria-label={`Network config: ${configInfo.config.label}`}
              as={RouterLink}
              color="LinkText"
              icon={<Edit />}
              size="xs"
              to={networkConfigsPath}
              variant="ghost"
            />
          </Tooltip>
        ) : (
          <Tooltip label="No network config assigned">
            <IconButton
              aria-label="No network config assigned"
              as={RouterLink}
              color="gray.400"
              icon={<FileCircleQuestion />}
              size="sm"
              to={networkConfigsPath}
              variant="ghost"
            />
          </Tooltip>
        ))}
    </Flex>
    <Box>
      <Flex fontSize="sm">
        <Text as="span" fontWeight="medium">
          MAC:{' '}
        </Text>
        <CopyableText value={iface.mac} />
      </Flex>
      <Text fontSize="sm">
        <Text as="span" fontWeight="medium">
          Addresses:{' '}
        </Text>
        {formatAddresses(iface.addresses)}
      </Text>
      {showVlan && iface.vlan && (
        <>
          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">
              Parent:{' '}
            </Text>
            {iface.vlan.parent}
          </Text>
          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">
              VLAN ID:{' '}
            </Text>
            {iface.vlan.id}
          </Text>
        </>
      )}
    </Box>
  </Box>
);

export const DeviceNetworkInterfaces = ({
  networkInterfaces,
  configsByInterface,
  networkConfigsPath,
  show = 'all',
}: Props) => {
  const embedded = show === 'physical';

  if (!networkInterfaces || networkInterfaces.length === 0) {
    return null;
  }

  const physicalInterfaces = networkInterfaces.filter((iface) => !iface.vlan);
  const vlanSubinterfaces = networkInterfaces.filter((iface) => iface.vlan);

  const getNetworkConfigsPath = (configInfo?: InterfaceConfigInfo) => {
    if (!networkConfigsPath || !configInfo) return networkConfigsPath;

    const params = writeTableStateToUrl(
      new URLSearchParams(),
      'DeviceNetworkConfigs',
      [],
      { pageIndex: 0, pageSize: 15 },
      [{ id: 'networkConfigId', value: String(configInfo.assignment.networkConfigId) }]
    );
    return `${networkConfigsPath}?${params.toString()}`;
  };

  return (
    <>
      {show !== 'vlan' && physicalInterfaces.length > 0 && (
        <Box
          border={embedded ? undefined : '2px solid'}
          borderColor="lightgrey"
          borderRadius="md"
          mb={embedded ? 0 : 6}
          p={embedded ? 0 : 5}
        >
          <Text as="h5" fontSize="md" fontWeight="semibold" mb={4}>
            Physical Interfaces
          </Text>
          <Flex flexWrap="wrap" gap={4}>
            {physicalInterfaces.map((iface) => (
              <InterfaceCard
                configInfo={configsByInterface?.get(iface.name)}
                iface={iface}
                key={iface.name}
                networkConfigsPath={getNetworkConfigsPath(configsByInterface?.get(iface.name))}
                showVlan={false}
              />
            ))}
          </Flex>
        </Box>
      )}
      {show !== 'physical' && vlanSubinterfaces.length > 0 && (
        <Box border="2px solid" borderColor="lightgrey" borderRadius="md" mb={6} p={5}>
          <Text as="h4" fontSize="lg" fontWeight="semibold" mb={4}>
            VLAN Subinterfaces
          </Text>
          <Flex flexWrap="wrap" gap={4}>
            {vlanSubinterfaces.map((iface) => (
              <InterfaceCard
                configInfo={configsByInterface?.get(iface.name)}
                iface={iface}
                key={iface.name}
                networkConfigsPath={getNetworkConfigsPath(configsByInterface?.get(iface.name))}
                showVlan
              />
            ))}
          </Flex>
        </Box>
      )}
    </>
  );
};
